import { type FC, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import {
  useGetFactoriesQuery,
  useUpgradeFactoryMutation,
} from '@api/queries/factory';
import { ConstructSucces } from '@common/components/ConstructSuccess/ConstructSuccess';
import { UpgradeBuild } from '@common/components/UpgradeBuild/UpgradeBuild';
import { useFactoryUpgradedData } from '@common/hooks/build-upgraded-data';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { firebaseLogUpgradeBuilding } from '@common/lib/firebaseLog';
import { updateStorageAndBalance } from '@redux/reducers/userReducer';
import { selectFactoryByIdOrName } from '@redux/selectors/factoryReducer';
import { createRequirementsList } from '@redux/selectors/lib';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { completeNewLevelStep } from '@/common/lib/completeNewLevelStep';
import { SendAnalitics } from '@/common/lib/playdeck';
import { selectNextStepPlant } from '@/redux/selectors/userSelectors';

const FactoryUpgrade: FC = () => {
  const [skip, setSkip] = useState(false);
  useGetFactoriesQuery(undefined, { skip, refetchOnMountOrArgChange: true });
  const { id } = useParams();
  const [isConstructComplete, setIsConstructComplete] = useState(false);
  const factory = useAppSelector((state) => selectFactoryByIdOrName(state, id));
  const [upgradeFactory, { isLoading }] = useUpgradeFactoryMutation();
  const currentStep = useAppSelector(selectNextStepPlant);
  const requirementsList = useMemo(
    () => createRequirementsList(factory?.nextLevelRequirements),
    [factory?.nextLevelRequirements],
  );
  const dispatch = useAppDispatch();
  const { isComplete } = useCheckRequirements(factory?.nextLevelRequirements);
  const localCompareOptions = useFactoryUpgradedData(factory?.id);
  const onQuarryUpgrade = useCallback((): void => {
    if (!factory) return;

    const { id: item_id, name: item, level: item_level } = factory;

    setSkip(true);
    upgradeFactory({ id: item_id }).then(({ data }) => {
      SendAnalitics.buildDone({
        type: 'materials',
        item_id,
        item,
        item_level,
      });
      dispatch(updateStorageAndBalance(requirementsList));
      setIsConstructComplete(true);
      completeNewLevelStep({
        data: { name: data?.name, level: data?.level },
        dispatch,
        currentStep: {
          id: currentStep?.id,
          name: currentStep?.name,
          level: currentStep?.level,
        },
      });
    });

    if (factory && factory.nextLevelRequirements)
      firebaseLogUpgradeBuilding(
        {
          name: factory.name,
          id: factory.id,
          level: factory.level + 1,
        },
        factory.nextLevelRequirements,
      );
  }, [factory?.id, upgradeFactory, requirementsList, dispatch]);

  if (!factory || !requirementsList) {
    return null;
  }

  return isConstructComplete ? (
    <ConstructSucces
      name={factory.name}
      kind="factory"
      id={factory.id}
      level={factory.level}
      compareData={localCompareOptions}
    />
  ) : (
    <>
      <Helmet>
        <title>Factory_Upgrade</title>
      </Helmet>
      <UpgradeBuild
        name={factory.name}
        id={factory.id}
        level={factory.level}
        kind="factory"
        isComplete={isComplete}
        onUpgrade={onQuarryUpgrade}
        isLoading={isLoading}
        requirementsList={requirementsList}
      />
    </>
  );
};

export { FactoryUpgrade };
